import { Framework } from '@/framework/instance';
import { ICollaboratorInfo, IEditMessage, IRealEditConfig } from '../types';
import { OperateType } from '../types/operate';
import { parseWsKey } from '../utils';
import { WsClient, WsTimer } from '../ws';
import { SyncBaseOperateAdapter } from './adapter/sync-base-operate';
import { SyncDocumentAdapter } from './adapter/sync-document';
import { SyncRenderIdsAdapter } from './adapter/sync-render-ids';
import { Collaborators } from './collaborators';
import { CRDTDocument } from './crdt-document';
import { UserReady } from './operate/user-ready';

export class Room extends Framework {
    /** 浏览器离开事件 */
    private beforeunloadHandler?: () => void;
    /** ws 实例 */
    private ws: WsClient | null = null;
    /** 重连次数 */
    private reconnectCount = 0;
    /** 编辑回调列表 */
    private messageCallbackMap: Record<number, ((data: IEditMessage<unknown>) => void)[]> = {};
    /** 截流定时器 */
    private throttleTimers: Record<number | string, number> = {};
    /** 基础信息 */
    private baseInfo: {
        /** 用户 id */
        uid: string | number;
        /** 群组 id, 可以使用 recordId */
        group: string | number;
        /** 用户 token */
        token: string;
    } = {
        uid: '',
        group: '',
        token: '',
    };
    /** 监听连接事件 */
    private connectedEvent = 'real-edit-connected';
    /** 监听 tick 事件 */
    private tickEvent = 'real-edit-tick';
    /** 监听 ready 事件 */
    private readyEvent = 'real-edit-ready';
    /** 监听成员变化事件 */
    private membersChangeEvent = 'real-edit-members-change';
    /** 监听超时事件 */
    private timeoutEvent = 'real-edit-timeout';
    /** 监听错误事件 */
    private errorEvent = 'real-edit-error';
    /** 监听渲染图层事件 */
    private userPickEvent = 'real-edit-user-pick';
    /** 监听渲染光标事件 */
    private userCursorEvent = 'real-edit-user-cursor';
    /** 时间偏移 */
    private offsetTime = 0;
    /** 定时器 */
    private tickTimer: WsTimer;
    /** 定时器列表 */
    timerList: Set<WsTimer> = new Set();
    /** 配置 */
    config?: IRealEditConfig;
    /** 协作成员 */
    collaborators: Collaborators;
    /** 用户就绪 */
    userReady: UserReady;
    /** 文档 */
    document: CRDTDocument;
    /** 同步渲染 id 适配器 */
    syncRenderIdsAdapter: SyncRenderIdsAdapter;
    /** 同步文档适配器 */
    syncDocumentAdapter: SyncDocumentAdapter;
    /** 同步基础信息适配器 */
    syncBaseOperateAdapter: SyncBaseOperateAdapter;

    constructor(
        identity: string,
        collaborators: Collaborators,
        options: {
            syncUpdateAt: (updateAt: number) => void;
        },
    ) {
        super(identity);
        this.collaborators = collaborators;
        this.userReady = new UserReady(identity, this, this.collaborators);
        this.document = new CRDTDocument(this.identity, this, this.collaborators);
        this.syncRenderIdsAdapter = new SyncRenderIdsAdapter(this.identity, this);
        this.syncDocumentAdapter = new SyncDocumentAdapter(this.identity, this, options);
        this.syncBaseOperateAdapter = new SyncBaseOperateAdapter(this.identity, this);
        this.tickTimer = new WsTimer('tickTimer');
        this.tickTimer.startLoop(() => {
            this.framework.events.emit(this.tickEvent);
        }, 1000);
    }

    /** 获取与服务器的时间偏移 */
    getOffsetTime() {
        return this.offsetTime;
    }

    /** 转换为服务器时间 */
    convertToServerTime(time: number) {
        return time + this.offsetTime;
    }

    /** 获取当前时间 */
    getCurrentDate() {
        return this.convertToServerTime(Date.now());
    }

    /** 是否是协同编辑 */
    isRealEdit() {
        return !!(this.collaborators.getLength() > 1 && this.hasWs() && this.isEnable());
    }

    /** 是否是多人在线编辑 (自己打开多个页面协同编辑不算多人编辑) */
    isMultMemberEdit() {
        return !!(this.collaborators.getOtherMembers().length > 1 && this.hasWs() && this.isEnable());
    }

    /** 是否开启协同编辑 */
    isEnable() {
        return this.config?.enable;
    }

    /** 设置配置 */
    setConfig(config: IRealEditConfig) {
        this.config = config;
        this.beforeunloadHandler = () => {
            this.leave();
        };
        window.addEventListener('beforeunload', this.beforeunloadHandler);
    }

    /** 获取配置 */
    getConfig() {
        return this.config;
    }

    /** 实例销毁 */
    dispose() {
        if (this.beforeunloadHandler) {
            window.removeEventListener('beforeunload', this.beforeunloadHandler);
        }
        for (const timer of this.timerList) {
            timer.destroy();
        }
        this.tickTimer.destroy();
    }

    /** 初始化数据 */
    destroy() {
        this.leave();
        this.document.destroy();
    }

    /** 渲染用户选中图层事件 */
    renderUserPick() {
        const members = this.collaborators
            .getMembers()
            .filter((item) => !item.getInfo().isCurrent && item.hasPickInfo())
            .map((item) => item.getInfo());
        this.framework.events.emit(this.userPickEvent, members);
    }

    /** 渲染用户光标 */
    renderUserCursor() {
        const members = this.collaborators
            .getMembers()
            .filter((item) => !item.getInfo().isCurrent)
            .map((item) => item.getInfo());
        // const members = this.collaborators.getMembers().map((item) => item.getInfo());
        this.framework.events.emit(this.userCursorEvent, members);
    }

    /** 监听消息事件 */
    onMessage(type: OperateType, cb: (data: IEditMessage<any>) => void) {
        this.messageCallbackMap[type] = this.messageCallbackMap[type] || [];
        this.messageCallbackMap[type].push(cb);
    }

    /** 加入房间 */
    join(uid: string | number, group: string | number, token: string) {
        this.leave();
        this.baseInfo = {
            uid,
            group,
            token,
        };
        if (!uid || !group || !token || !this.config) return;
        const is_debug = this.config.isWSDebug;
        this.reconnectCount = 0;
        const host = this.config.wsAddress;
        const startTime = Date.now();
        this.ws = new WsClient(this.identity, {
            host,
            uid,
            group,
            token,
            is_debug,
        });
        this.ws.onReady((wsMemberKey: string) => {
            this.reconnectCount = 0;
            this.collaborators.setWsKey(wsMemberKey);
            // 重写获取服务端时间
            this.offsetTime = this.collaborators.getServerTime() - startTime;
            this.fetchOnlineMembers().then(() => {
                // 触发连接事件
                this.framework.events.emit(this.connectedEvent);
            });
            // 关闭协同后，需要马上触发开启 ready
            if (!this.isEnable()) {
                this.document.setReady();
                this.emitReady();
            }
        });
        this.ws.onClose((closeReconnect: boolean, closeFromServer: boolean) => {
            // 断线重连
            if (closeReconnect) {
                console.log('ws close reconnect...');
                this.reconnect();
            } else {
                // 被服务器/主动断开，不需要重连
                if (closeFromServer) {
                    this.leave();
                    this.framework.events.emit(this.timeoutEvent, {
                        from: '-被服务器/主动断开，不需要重连',
                    });
                }
            }
        });
        this.ws.onEdit((content, fromWsMemberKey) => {
            // console.log(
            //     'ws Collaborator onEdit',
            //     this.collaborators.isMaster,
            //     this.collaborators.currentWsKey,
            //     this.collaborators.memberKeys,
            //     content,
            //     fromWsMemberKey
            // );
            const type = content[0];
            const data = content[1];
            const toWsKeys = content[2];
            const isToMaster = content[3];
            const latestActiveTime = content[4];
            if (latestActiveTime) {
                this.collaborators.getMember(parseWsKey(fromWsMemberKey).wsKey)?.setLastActiveTime(latestActiveTime);
            }
            if (!type) {
                // console.log('ot ws Collaborator onEdit 1', type);
                return;
            }
            if (isToMaster && !this.collaborators.isMaster()) {
                // console.log('ot ws Collaborator onEdit 2', isToMaster, !this.collaborators.isMaster);
                return;
            }
            if (toWsKeys && toWsKeys.length && toWsKeys.indexOf(this.collaborators.getWsKey()) === -1) {
                // console.log('ot ws Collaborator onEdit 3', toWsKeys, this.collaborators.getWsKey());
                return;
            }
            const callbacks = this.messageCallbackMap[type];
            if (callbacks) {
                for (const cb of callbacks) {
                    cb({ fromWsMemberKey, data });
                }
            }
        });
        this.ws.onMembersChange(() => {
            this.fetchOnlineMembers();
        });
        this.ws.connect();
    }

    /** 监听连接事件 */
    onConnected(cb: () => void) {
        return this.framework.events.on(this.connectedEvent, cb);
    }
    /** 监听 ready 事件 */
    onReady(cb: () => void) {
        return this.framework.events.on(this.readyEvent, cb);
    }
    /** 监听成员变化事件 */
    onMembersChange(cb: (members: ICollaboratorInfo[]) => void) {
        return this.framework.events.on(this.membersChangeEvent, cb);
    }
    /** 监听超时事件 */
    onTimeout(cb: (data: { time?: number; from: string }) => void) {
        return this.framework.events.on(this.timeoutEvent, cb);
    }
    /** 监听错误事件 */
    onError(cb: () => void) {
        return this.framework.events.on(this.errorEvent, cb);
    }
    /** 监听用户选中图层事件 */
    onUserPick(cb: (members: ICollaboratorInfo[]) => void) {
        return this.framework.events.on(this.userPickEvent, cb);
    }
    /** 监听用户光标事件 */
    onUserCursor(cb: (members: ICollaboratorInfo[]) => void) {
        return this.framework.events.on(this.userCursorEvent, cb);
    }
    /** 监听 tick 事件 */
    onTick(cb: () => void) {
        return this.framework.events.on(this.tickEvent, cb);
    }

    /** 离开房间 */
    leave() {
        this.offsetTime = 0;
        this.collaborators.dispose();
        for (const timer of this.timerList) {
            timer.destroy();
        }
        this.timerList.clear();
        this.throttleTimers = {};
        this.ws?.close();
        this.ws = null;
        this.framework.events.emit(this.membersChangeEvent, []);
        this.renderUserCursor();
    }

    /** 截流发送 */
    throttleSendToAll(
        type: OperateType | { sendType: OperateType; throttleType: string },
        data?: unknown,
        forceUpdate = false,
        throttleTime = 33,
        beforeSend?: (data?: any) => boolean,
    ) {
        const now = Date.now();
        let sendType = type as OperateType;
        let throttleType = type as unknown as string;
        if (typeof type === 'object') {
            sendType = type.sendType;
            throttleType = type.throttleType;
        }
        const lastTime = this.throttleTimers[throttleType] || 0;
        if (forceUpdate || now - lastTime >= throttleTime) {
            this.throttleTimers[throttleType] = now;
            if (beforeSend) {
                const res = beforeSend(data);
                if (!res) return;
            }
            this.sendToAll(sendType, data);
        }
    }

    /** 发送给所有人 */
    async sendToAll(type: OperateType, data?: unknown) {
        if (!this.isRealEdit()) return;
        const lastActiveTime = this.getCurrentDate();
        this.collaborators.getMember()?.setLastActiveTime(lastActiveTime);
        await this.ws?.send([type, data || 0, 0, 0, lastActiveTime]);
    }

    /** 发送给指定用户 */
    async sendToUser(wsKeys: number[], type: OperateType, data?: unknown) {
        if (!this.isRealEdit()) return;
        await this.ws?.send([type, data || 0, wsKeys]);
    }

    /** 发送给管理员 */
    async sendToMaster(type: OperateType, data?: unknown) {
        if (!this.isRealEdit()) return;
        await this.ws?.send([type, data || 0, 0, 1]);
    }

    /** 触发 ready 事件 */
    emitReady() {
        this.framework.events.emit(this.readyEvent);
    }

    /** 是否存在 ws */
    private hasWs() {
        return !!this.ws;
    }

    /** 重连 */
    private reconnect() {
        if (this.reconnectCount >= 5) {
            this.framework.events.emit(this.errorEvent);
        } else {
            const timer = new WsTimer('reconnectTimer');
            this.timerList.add(timer);
            timer.start(() => {
                this.timerList.delete(timer);
                timer.destroy();
                console.log('ws reconnect...', this.reconnectCount);
                this.ws?.connect();
            }, 3000 * this.reconnectCount);
            this.reconnectCount++;
        }
    }

    /** 获取在线成员 */
    private async fetchOnlineMembers() {
        if (!this.config) return;
        try {
            const remoteData = await this.config.getOnlineMembers(this.baseInfo.group);
            const result = this.collaborators.setOnlineMembersData(remoteData);
            if (result) {
                const infos = this.collaborators.getMembers().map((item) => item.getInfo());
                this.framework.events.emit(this.membersChangeEvent, infos);
                this.syncDocumentAdapter.processDocument();
                // 给新加入的成员发送同步文档数据
                console.log('ot fetchOnlineMembers', { isLastActiveMember: this.collaborators.isLastActiveMember() });
                if (this.collaborators.isLastActiveMember()) {
                    if (this.userReady.send()) {
                        this.document.setReady();
                        this.emitReady();
                    }
                    const newMemberWsKeys = infos
                        .filter((item) => !item.isReady && !item.isCurrent)
                        .map((item) => item.wsKey);
                    console.log('ot sync doc wskeys', { newMemberWsKeys });
                    if (newMemberWsKeys.length) {
                        await this.syncDocumentAdapter.syncDocument(newMemberWsKeys);
                    }
                }
            }
        } catch (e) {
            console.error('fetchOnlineMembers', e);
            this.framework.events.emit(this.errorEvent);
            this.leave();
        } finally {
            this.renderUserCursor();
            this.renderUserPick();
        }
    }
}
